import { BaseAction } from "../../../core/actions/BaseAction";
import { getRuntimeConfig } from "../../../Lib/Config/RuntimeConfig";
import { IRedirectToPaymentGatewayParams } from "../../../Orchestrator/Types/Interface/IPaymentProcess";
import { updateRecoilState, getRecoilStateValue } from "../../../Orchestrator/Recoils/Events/Recoil";
import { showNotification } from "../../../Services/notificationService";
import { getTranslatedMessage } from "../../../Lib/Utils/i18n";
import { buildSessionAncillaries } from "../Addons/buildSessionAncillaries";
import { buildSessionRefundLegs } from "../../../Components/OnwardTravel/sessionRefundLegs";
import { navigateToBookingDetails, emitBookingConfirmationAnalytics, readConfirmationFlowFlags } from "./PaymentGatewayCheckoutAction";
import { getBooking } from "../../../app/api/getBooking";
import * as Sentry from "@sentry/nextjs";
import { IPaymentCard } from "../../../Orchestrator/Types/Interface/IPaymentCards";
import { parseStoredCardId } from "../../../Lib/Utils/storedCards";
const MPGS_SCRIPT_URL = process.env.NEXT_PUBLIC_MPGS_GATEWAY_URL
    || "https://ap-gateway.mastercard.com/static/checkout/checkout.min.js";

declare global {
    interface Window {
        Checkout?: {
            configure: (config: any) => void;
            showEmbeddedPage: (selector: string) => void;
            showPaymentPage: () => void;
        };
    }
}

function loadMpgsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (window.Checkout) {
            resolve();
            return;
        }
        const existing = document.querySelector(`script[src="${MPGS_SCRIPT_URL}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve(), { once: true });
            return;
        }
        const script = document.createElement("script");
        script.src = MPGS_SCRIPT_URL;
        script.async = true;
        script.setAttribute("data-error", "mpgsErrorCallback");
        script.setAttribute("data-cancel", "mpgsCancelCallback");
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load MPGS checkout script"));
        document.head.appendChild(script);
    });
}

class RedirectToPaymentGatewayAction extends BaseAction<string, IRedirectToPaymentGatewayParams> {
    // PaymentInitStep emits Payment.RedirectToGateway from each Payment.Click
    // handler that joined the upstream dedup. Without this, every joined click
    // re-fires the /api/payment/session POST and reopens the MPGS lightbox.
    protected override deduplicate = true;

    public async run(): Promise<string> {
        const { amount, mail, bookingId, accessToken } = this.params!;
        const isAnonymous = getRecoilStateValue<boolean>("isAnonymousStateAtom") || false;
        // Guests no longer carry a token (none is fetched or sent), so accessToken
        // is only required for logged-in users; guest checkout proceeds without it.
        if (amount >= 0 && mail && bookingId && (accessToken || isAnonymous)) {
            try {
                const config = await getRuntimeConfig();
                // beef redirect flow: the backend now IGNORES returnUrl/cancelUrl —
                // redirect targets come from its payment_client_redirects table
                // (result: /checkout/callback, cancel: /checkout/cancel). Kept in
                // the body only for pre-deploy backends; safe to remove once the
                // beef redirect flow is live everywhere.
                const returnUrl = `${window.location.origin}/checkout/callback`;
                const cancelUrl = `${window.location.origin}/checkout/cancel`;
                const saveCard = !isAnonymous && (getRecoilStateValue<boolean>("saveCardAtom") || false);
                // Paying with a card already stored on the account: the radio
                // value carries its id (see STORED_CARD_PREFIX in Payment.tsx).
                // The gateway session is created against that card instead of
                // collecting new card details in the MPGS drop-in.
                //
                // paymentStateAtom is persisted, so a card picked in an
                // abandoned checkout can outlive the card itself (deleted in My
                // Account, or expired). Re-check the id against the loaded card
                // list here rather than in the UI — at Pay-click the list has
                // definitely loaded, whereas on mount it is still empty. An id
                // that no longer resolves falls back to a normal new-card
                // session instead of failing at the gateway.
                const method = getRecoilStateValue<{ method?: string }>("paymentStateAtom")?.method;
                const selectedCardId = parseStoredCardId(method);
                const storedCards = getRecoilStateValue<IPaymentCard[]>("paymentCardsAtom") || [];
                const cardId = storedCards.some((card) => card.id === selectedCardId) ? selectedCardId : "";
                // Ancillaries to be refunded as part of this gateway session
                // (deselected baseline extras queued in
                // pendingRefundFulfillmentIdsAtom). Backend reads the field
                // as `refundedAncillaries` and processes the refund on
                // settlement alongside the new charge.
                const refundedAncillaries = buildSessionAncillaries(bookingId);
                // Onward products (shuttle, Nol) on a leg whose date is moving:
                // they stay on the original date, so the change-date cancels and
                // refunds them with the same settlement that pays for the new
                // train. Empty on every other flow.
                const refundLegs = buildSessionRefundLegs(bookingId);
                // Traceability: a refund settles server-side and raises no
                // browser error if it goes wrong, so this is the only client-side
                // record tying a bad settlement back to exactly which
                // fulfillments were sent for auto-cancellation.
                //
                // captureMessage, NOT addBreadcrumb: a breadcrumb only reaches
                // Sentry attached to an event, and a bad settlement raises no
                // browser exception — the record would exist only in the one
                // case we don't need it. UUIDs only, no PII.
                //
                // Recorded AFTER the POST resolves, with the status: written
                // beforehand it would claim "sent" for sessions the backend
                // rejected, which is exactly the grep you do not want to
                // mistrust when investigating a bad settlement.
                const recordRefundLegsSent = (status: number) => {
                    if (refundLegs.length === 0) return;
                    Sentry.captureMessage("refundLegs sent on payment session", {
                        level: "info",
                        tags: { feature: "cdt-onward-auto-cancel" },
                        extra: {
                            bookingId,
                            // Kept for correlation when investigating a bad
                            // settlement. A zero total USED to take a backend
                            // path that refunded nothing and never confirmed the
                            // exchange; that was fixed backend-side and a 0.00
                            // change-date now cancels and refunds like any
                            // other.
                            amount,
                            status,
                            refundLegs: refundLegs.map((leg) => ({
                                bookingId: leg.bookingId,
                                fulfillmentIds: leg.fulfillmentIds,
                            })),
                        },
                    });
                };
                const body = {
                    amount: amount,
                    currency: "AED",
                    id: bookingId,
                    customerEmail: mail,
                    returnUrl: returnUrl,
                    cancelUrl: cancelUrl,
                    saveCard: saveCard,
                    // ALWAYS sent, even empty. Omitting it makes beef answer 500
                    // (prelive 2026-08-05, booking 11edab01) — the field is
                    // required by its DTO.
                    refundedAncillaries,
                    // OMITTED, not sent empty. Its presence puts beef on a refund
                    // branch, so every guest checkout, manage-seating, upgrade and
                    // plain booking would exercise that path for no benefit. The
                    // key exists only when there is something to refund.
                    ...(refundLegs.length > 0 ? { refundLegs } : {}),
                    ...(cardId && { cardId }),
                };
                const response = await fetch(
                    '/api/payment/session',
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            // Guests send neither Authorization nor Requestor; logged-in users do.
                            ...(!isAnonymous && { Authorization: `Bearer ${accessToken}` }),
                            ...(!isAnonymous && { "Requestor": config.NEXT_PUBLIC_REQUESTOR })
                        },
                        body: JSON.stringify(body),
                    }
                ).catch((err) => {
                    // `fetch` rejects for two very different reasons: the
                    // request never left (offline, DNS, CORS), or it did and the
                    // connection died after the server had already settled. The
                    // second is exactly the bad-settlement case this record
                    // exists for, and nothing here can tell the two apart — so
                    // record either way and let `status: 0` say "unknown".
                    recordRefundLegsSent(0);
                    throw err;
                });
                recordRefundLegsSent(response.status);

                if (response.status === 204) {
                    // Zero-amount / idempotent-finalized settlement: no MPGS
                    // callback ever fires, so emit the confirmation analytics
                    // (#7/#8/#9) here. Fire-and-forget so the booking fetch never
                    // delays navigation; the helper dedupes per bookingId via
                    // sessionStorage, so it's safe if any other path also runs.
                    // Capture the flow flags SYNCHRONOUSLY now — navigateToBookingDetails
                    // (below) clears them, and the emit resolves after that.
                    const confirmationFlags = readConfirmationFlowFlags();
                    void (async () => {
                        try {
                            const resp = await getBooking(bookingId, false);
                            const bookingResponse = resp.status === "ok" ? resp.answer : null;
                            emitBookingConfirmationAnalytics(bookingId, bookingResponse, confirmationFlags);
                        } catch (e) {
                            console.error("zero-amount confirmation analytics failed", e);
                        }
                    })();
                    navigateToBookingDetails(bookingId);
                    return "";
                }

                const data = await response.json();
                if (!response.ok) {
                    showNotification("error", getTranslatedMessage("checkout.payment.error"), "");
                    return "";
                }
                if (data.session?.id) {
                    sessionStorage.setItem("checkoutFromWithLogin", "false");
                    sessionStorage.setItem("guestCheckoutBookingId", bookingId);
                    updateRecoilState("guestCheckoutBookingIdAtom", "");

                    // PAYMENT_TEST_MODE bypass. When beef returns testMode: true,
                    // the session id (TEST-SESSION-…) is synthetic and the real
                    // MPGS lightbox would reject it. Skip loading checkout.js and
                    // skip Checkout.configure/showPaymentPage — instead navigate
                    // straight to the same returnUrl MPGS would have redirected
                    // to after a successful payment, so beef's sessionCheckout
                    // handler sees an identical request and runs the real Turnit
                    // external-payment call against the stubbed approved order.
                    if (data.testMode === true && data.successIndicator) {
                        const sep = returnUrl.includes("?") ? "&" : "?";
                        window.location.assign(
                            `${returnUrl}${sep}resultIndicator=${encodeURIComponent(data.successIndicator)}&checkoutVersion=1.0.0`,
                        );
                        return "";
                    }

                    await loadMpgsScript();

                    (window as any).mpgsErrorCallback = (error: any) => {
                        console.error("MPGS payment error:", error);
                        showNotification("error", getTranslatedMessage("checkout.payment.error"), "");
                    };

                    (window as any).mpgsCancelCallback = () => {
                        showNotification("info", getTranslatedMessage("checkout.payment.cancelled") || "Payment cancelled", "");
                    };

                    window.Checkout!.configure({
                        session: { id: data.session.id },
                    });

                    window.Checkout!.showPaymentPage();

                    // Return empty — MPGS opens payment page directly, callback page handles return
                    return "";
                }
                else {
                    // 2xx with no session.id is the backend's "no MPGS drop-in
                    // needed — everything was settled server-side" signal.
                    // Per the swagger, `session` is explicitly nullable; the
                    // field exists only to bootstrap the MPGS drop-in. Absent
                    // session = no lightbox to open. Covers any zero-pay
                    // scenario where /api/payment/session does the work
                    // synchronously (manage-seating refund-only, full-voucher
                    // coverage, full-pass coverage with 0 residual, etc.).
                    // Treat the same as 204 (the sibling branch above) —
                    // navigate to booking details so the user isn't stranded
                    // on /checkout.
                    navigateToBookingDetails(bookingId);
                    return "";
                }
            } catch (err) {
                console.error(err);
            }
            return "";
        }
        return "";
    }
}

export const redirectToPaymentGatewayAction = new RedirectToPaymentGatewayAction();
